import React from 'react'
import { useTranslation } from 'react-i18next'
import emailjs from 'emailjs-com'
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card'
import { Input } from './components/ui/input'
import { Textarea } from './components/ui/textarea'
import { Button } from './components/ui/button'

type SendStatus = 'idle' | 'sending' | 'sent' | 'error'

const Contact = () => {
    const { t } = useTranslation()
    const [ status, setStatus ] = React.useState<SendStatus>('idle')

    const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // React clears currentTarget once the handler returns, so hold on to the
        // form here for the reset that runs after the send resolves.
        const form = e.currentTarget

        setStatus('sending')

        emailjs.sendForm('gmail', 'template_oTnayuRN', form, 'user_3e0VIkZBbR28aUmEVEajw')
        .then(() => {
            form.reset()
            setStatus('sent')
        }, () => {
            setStatus('error')
        })
    }

    const clearSettledStatus = () => setStatus((current) => (
        current === 'sent' || current === 'error' ? 'idle' : current
    ))

    const isSending = status === 'sending'

    const feedback = {
        idle: { text: '', tone: 'text-muted-foreground' },
        sending: { text: t('contact.sendingStatus'), tone: 'text-muted-foreground' },
        sent: { text: t('contact.sent'), tone: 'text-foreground font-medium' },
        error: { text: t('contact.error'), tone: 'text-destructive' },
    }[status]

    return (
        <section className="bg-[hsl(220,26%,14%)] py-16">
            <div className="mx-auto max-w-2xl px-4 sm:px-6">
                <Card className="shadow-md">
                    <CardHeader>
                        <CardTitle className="text-center">{t('contact.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form
                            onSubmit={sendEmail}
                            onChange={clearSettledStatus}
                            className="space-y-4"
                        >
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label htmlFor="name" className="text-sm font-medium">{t('contact.name')}</label>
                                    <Input type="text" name="name" id="name" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="email" className="text-sm font-medium">{t('contact.email')}</label>
                                    <Input type="email" name="email" id="email" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="message" className="text-sm font-medium">{t('contact.message')}</label>
                                <Textarea name="message" id="message" rows={6} />
                            </div>
                            <Button type="submit" className="w-full sm:w-auto" disabled={isSending}>
                                {isSending ? t('contact.sending') : t('contact.send')}
                            </Button>
                            <p role="status" aria-live="polite" className={`text-sm min-h-5 ${feedback.tone}`}>
                                {feedback.text}
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </section>
    )
}

export default Contact
